// =============================================================================
// FILE PATH: src/modules/pms/components/ProjectDetail.js
// =============================================================================

import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import WBSTemplateDrawer from "../wbs_template/components/WBSTemplateDrawer";

import {
  getWBSTree,
  createWBSNode,
  createWBSDependency,
  getWBSDependencies, // 🔥 ADDED
  updateWBSNode,
  deleteWBSNode
} from "../services/wbsService";

import WBSTree from "../components/WBSTree";
import WBSFormModal from "../components/WBSFormModal";

export default function ProjectDetail({ projectId: propProjectId }) {

  const params = useParams();
  const projectId = propProjectId || params.projectId;

  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [parentId, setParentId] = useState(null);
  // 🔥 EDIT STATE
  const [editNode, setEditNode] = useState(null);

  // 🔥 NEW: dependency states
  const [selectedTask, setSelectedTask] = useState(null);
  const [dependencyOpen, setDependencyOpen] = useState(false);
  const [selectedPredecessor, setSelectedPredecessor] = useState("");

  // 🔥 ADDED: dependency data + maps
  const [dependencies, setDependencies] = useState([]);
  const [predecessorMap, setPredecessorMap] = useState({});
  const [successorMap, setSuccessorMap] = useState({});

  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  const [templateDrawerOpen, setTemplateDrawerOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);

  // =========================================================
  // LOAD TREE
  // =========================================================
  const loadTree = async () => {
    try {
      setLoading(true);

      const res = await getWBSTree(projectId);
      const treeData = res.data.data || [];
      setTree(treeData);

      // 🔥 ADDED: LOAD DEPENDENCIES
      try {
        const depRes = await getWBSDependencies(projectId);
        const depData = depRes.data.data || [];
        setDependencies(depData);

        const predMap = {};
        const succMap = {};

        depData.forEach(dep => {

          if (!predMap[dep.successor_id]) {
            predMap[dep.successor_id] = [];
          }
          predMap[dep.successor_id].push(dep.predecessor_id);

          if (!succMap[dep.predecessor_id]) {
            succMap[dep.predecessor_id] = [];
          }
          succMap[dep.predecessor_id].push(dep.successor_id);

        });

        setPredecessorMap(predMap);
        setSuccessorMap(succMap);

      } catch (depErr) {
        console.error("Error loading dependencies", depErr);
        setDependencies([]);
        setPredecessorMap({});
        setSuccessorMap({});
      }

    } catch (err) {
      console.error("Error loading WBS tree", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) loadTree();
  }, [projectId]);

  // =========================================================
  // 🔥 HANDLE DEPENDENCY CLICK
  // =========================================================
  const handleDependency = (node) => {
    setSelectedTask(node);
    setSelectedPredecessor("");
    setDependencyOpen(true);
  };

  // =========================================================
  // 🔥 EDIT HANDLER
  // =========================================================
  const handleEdit = (node) => {
    setEditNode(node);
    setModalOpen(true);
  };

  // =========================================================
// 🔥 DELETE HANDLER
// =========================================================
const handleDelete = async (node) => {

  const confirmDelete = window.confirm(
    `Are you sure you want to delete "${node.name}"?`
  );

  if (!confirmDelete) return;

  try {
    await deleteWBSNode(node.id);

    alert("Node deleted successfully");

    loadTree();

  } catch (err) {
    alert(err?.response?.data?.message || "Delete failed");
  }
};

  // =========================================================
  // 🔥 SAVE DEPENDENCY
  // =========================================================
  const handleSaveDependency = async () => {
    try {
      if (!selectedPredecessor) {
        alert("Please select predecessor");
        return;
      }

      await createWBSDependency({
        project_id: projectId,
        predecessor_id: selectedPredecessor,
        successor_id: selectedTask.id,
        dependency_type: "FS"
      });

      alert("Dependency created");

      setDependencyOpen(false);
      loadTree();

    } catch (err) {
      alert(err?.response?.data?.message || "Error creating dependency");
    }
  };

  // =========================================================
  // FLATTEN TREE FOR DROPDOWN
  // =========================================================
  const flattenTasks = (nodes, list = []) => {
    nodes.forEach(n => {
      if (n.type === "TASK") {
        list.push(n);
      }
      if (n.children) flattenTasks(n.children, list);
    });
    return list;
  };

  const allTasks = flattenTasks(tree);

  // =========================================================
  // FULLSCREEN
  // =========================================================
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handler = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // =========================================================
  // ADD NODE
  // =========================================================
  const handleAdd = (pid) => {
    setParentId(pid);
    setEditNode(null); // 🔥 ensure add mode
    setModalOpen(true);
  };

  // =========================================================
  // SAVE NODE
  // =========================================================
  const handleSave = async (payload) => {
    try {
      //await createWBSNode(payload);
      //setModalOpen(false);
      //loadTree();
      if (payload.node_id) {
        await updateWBSNode(payload);
      } else {
        await createWBSNode(payload);
      }

      setModalOpen(false);
      setEditNode(null); // 🔥 reset
      loadTree();

    } catch (err) {
      console.error("Error creating node", err);
    }
  };

  return (
    <div style={styles.container}>

      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Project WBS</h2>
          <div style={styles.subText}>Project ID: {projectId}</div>
        </div>

        <div style={styles.headerActions}>
          <button style={styles.refreshBtn} onClick={loadTree}>🔄 Refresh</button>
          <button style={styles.fullBtn} onClick={toggleFullscreen}>
            {isFullscreen ? "🗗 Restore" : "⛶ Maximize"}
          </button>
          <button style={styles.addBtn} onClick={() => handleAdd(null)}>
            + Add Root
          </button>

          <button
            onClick={() => setTemplateDrawerOpen(true)}
            style={{
              padding: "8px 14px",
              background: "#388e3c",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              marginLeft: "10px"
            }}
          >
            + Add from Template
          </button>
        </div>
      </div>

      {/* TREE */}
      <div ref={containerRef} style={styles.card}>
        {loading ? (
          <div style={styles.loader}>Loading WBS...</div>
        ) : (
          <WBSTree
            data={tree}
            onAdd={handleAdd}
            onDependency={handleDependency}
             onSelect={setSelectedNode}   // 🔥 NEW
             selectedNode={selectedNode} // 🔥 NEW
            onEdit={handleEdit}
            onDelete={handleDelete}

            // 🔥 ADDED
            predecessorMap={predecessorMap}
            successorMap={successorMap}
          />
        )}
      </div>

      {/* CREATE NODE MODAL */}
      <WBSFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        parentId={parentId}
        projectId={projectId}
        editNode={editNode}
        isEdit={!!editNode}
      />

      {/* 🔥 DEPENDENCY MODAL */}
      {dependencyOpen && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3>Select Predecessor</h3>

            <select
              style={styles.input}
              value={selectedPredecessor}
              onChange={(e) => setSelectedPredecessor(e.target.value)}
            >
              <option value="">-- Select Task --</option>

              {allTasks
                .filter(t => t.id !== selectedTask?.id)
                .map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </select>

            <div style={styles.actions}>
              <button onClick={() => setDependencyOpen(false)}>
                Cancel
              </button>

              <button onClick={handleSaveDependency}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

          {/* 🔥 TEMPLATE DRAWER */}
      <WBSTemplateDrawer
        open={templateDrawerOpen}
        onClose={() => setTemplateDrawerOpen(false)}
        projectId={projectId}
        selectedNode={selectedNode}
        onSuccess={loadTree}
      />
    </div>
  );
}

// ==========================================================
// STYLES (ADDED MODAL ONLY)
// ==========================================================
const styles = {
  container: { padding: 20, background: "#f9fafb", minHeight: "100vh" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  title: { margin: 0, fontSize: 22, fontWeight: 600 },
  subText: { fontSize: 12, color: "#6b7280" },
  headerActions: { display: "flex", gap: 10 },
  addBtn: { padding: "8px 14px", background: "#4f46e5", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" },
  refreshBtn: { padding: "8px 12px", background: "#e5e7eb", border: "none", borderRadius: 6 },
  fullBtn: { padding: "8px 12px", background: "#e5e7eb", border: "none", borderRadius: 6 },
  card: { background: "#fff", borderRadius: 8, padding: 10, boxShadow: "0 2px 6px rgba(0,0,0,0.1)" },
  loader: { padding: 20, textAlign: "center" },
  overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center" },
  modal: { background: "#fff", padding: 20, borderRadius: 8, width: 300 },
  input: { width: "100%", padding: 8, marginTop: 10 },
  actions: { marginTop: 15, display: "flex", justifyContent: "flex-end", gap: 10 }
};